import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { Feature } from '@/constants/features';
import { FilterKeys, FilterLabels } from '../../../../components/Filters';
import { ASSETS } from '../../../../components/Filters/filterTypes';
import {
  BASINS_WITH_FILTER_QUERY,
  COMPANIES_WITH_FILTER_QUERY,
  COUNTRIES_WITH_FILTER_QUERY,
} from '../../../../components/Filters/operations';
import { M_A_OPPORTUNITIES_SUBSCRIPTION } from '../../../../utils/constants';

export const GOOGLE_ANALYTICS_TAG = Feature.MaAnalytics;

export const MaSliderKeys = {
  valuation: 'valuation',
  reserves: 'reserves',
  production: 'production',
  emissions: 'emissions',
};
export const MaFilterKeys = {
  acquisitionCostMax: 'maxAcquisitionCost',
  acquisitionCostMin: 'minAcquisitionCost',
  pricePerBarrelMax: 'maxPriceFlowingBarrel',
  pricePerBarrelMin: 'minPriceFlowingBarrel',
  ...MaSliderKeys,
};

export const MaFilterMaxKeys = {
  valuation: 'maxValuation',
  reserves: 'maxReserves',
  production: 'maxProduction',
  emissions: 'maxEmissions',
};

export const MaFilterLabels = {
  valuation: 'NPV10 (US$MM)',
  reserves: 'Reserves (MMboe)',
  production: 'Production (boe/d)',
  emissions: 'Emissions (kgCO2e/boe)',
  dates: 'EFFECTIVE DATE (YEARS)',
  includeNegativeValuations: 'Include Negative Valuations',
  acquisitionCostMax: 'Acquisition Cost Max',
  acquisitionCostMin: 'Acquisition Cost Min',
  pricePerBarrelMax: 'Price per Flowing Barrel Max',
  pricePerBarrelMin: 'Price per Flowing Barrel Min',
  acquisition: 'Acquisition Cost (US$/boe)',
  price: 'Price per Flowing Barrel (US$/boe)',
  excludeOutliers: 'Exclude Outliers',
};

const getAssetsPropVariables = filters => ({
  countries: filters[FilterKeys.countries]?.map(c => c.isoCode) || [],
  companies: filters[FilterKeys.companiesSeller]?.map(c => c.name) || [],
  basins: filters[FilterKeys.basins]?.map(b => b.name) || [],
  operator: false,
  nonOperator: false,
  includeHistoricalParticipants: false,
  isMAndAOpportunity: true,
  feature: M_A_OPPORTUNITIES_SUBSCRIPTION,
});

const getSellerCompaniesPropVariables = filters => ({
  assets: [],
  countries: filters[FilterKeys.countries]?.map(c => c.isoCode) || [],
  companies: filters[FilterKeys.companiesSeller]?.map(c => c.name) || [],
  basins: filters[FilterKeys.basins]?.map(b => b.name) || [],
  operator: false,
  nonOperator: false,
  includeHistoricalParticipants: false,
  activeInBlocks: false, // we are only fetching assets M&A opportunities
  activeInLeases: false,
  isMAndAOpportunity: true,
  feature: M_A_OPPORTUNITIES_SUBSCRIPTION,
  maBuyers: false,
  maSellers: false,
});

export const getCountriesPropVariables = filters => {
  return {
    companies: filters[FilterKeys.companiesSeller]?.map(c => c.name) || [],
    assets: [],
    basins: filters[FilterKeys.basins]?.map(b => b.name) || [], // the query expects an array of basin names instead of ids
    operator: false,
    nonOperator: false,
    hasAssets: true,
    hasBlocks: true,
    hasLeases: true,
    hasGhgEmissions: false,
    hasMaTransactions: false,
    includeHistoricalParticipants: true,
    feature: M_A_OPPORTUNITIES_SUBSCRIPTION,
  };
};

const getBasinsPropVariables = filters => ({
  countries: filters[FilterKeys.countries]?.map(c => c.isoCode) || [],
  companies: filters[FilterKeys.companiesSeller]?.map(c => c.name) || [],
  assets: [],
  operator: false,
  nonOperator: false,
  feature: M_A_OPPORTUNITIES_SUBSCRIPTION,
  isMAndAOpportunity: true,
  hasMaTransactions: false,
});

const SELLER_COMPANIES = {
  key: FilterKeys.companiesSeller,
  name: FilterLabels.companiesSeller,
  multiple: true,
  optionKey: 'name',
  query: COMPANIES_WITH_FILTER_QUERY,
  queryVariable: 'search',
  runQueryAfterOnChange: true,
  enablePagination: true,
  responseCallback: res => res?.companies || [],
  control: {
    controlItems: [
      {
        key: FilterKeys.operator,
        name: 'Operator',
        label: FilterLabels.operator,
        option: {
          name: 'Operator',
        },
      },
      {
        key: FilterKeys.includeHistoricalParticipants,
        name: 'Historical Participants',
        label: FilterLabels.includeHistoricalParticipants,
        option: {
          name: 'Historical Participants',
        },
      },
    ],
    getControlValuesFromFilters: filters => ({
      controlValues: {
        [FilterKeys.operator]: filters[FilterKeys.operator],
        [FilterKeys.includeHistoricalParticipants]:
          filters[FilterKeys.includeHistoricalParticipants],
      },
    }),
  },
  propVariables: getSellerCompaniesPropVariables,
};

export const BASINS = {
  name: FilterLabels.basins,
  key: FilterKeys.basins,
  query: BASINS_WITH_FILTER_QUERY,
  menuItems: null,
  menuItemsCallback: null,
  optionKey: 'name',
  multiple: true,
  propVariables: getBasinsPropVariables,
  queryVariable: 'search',
  runQueryAfterOnChange: true,
  responseCallback: res => res?.basins || [],
};

const COUNTRIES = {
  excelLike: true,
  groupByName: 'Region',
  groupOptionKey: 'region',
  key: FilterKeys.countries,
  mapQueryData: data => data?.countriesWithFilter,
  multiple: true,
  name: FilterLabels.countries,
  optionKey: 'displayName',
  propVariables: getCountriesPropVariables,
  query: COUNTRIES_WITH_FILTER_QUERY,
  queryVariable: null,
  runQueryAfterOnChange: false,
};

// In the Opportunities tab, the filter types are slightly different,
// specially the queries use to fetch the data.
export const FILTER_TYPES = [
  COUNTRIES,
  SELLER_COMPANIES,
  {
    ...ASSETS,
    propVariables: getAssetsPropVariables,
  },
  BASINS,
];

export const ACQUISITION_COST_MIN_MAX = {
  label: MaFilterLabels.acquisition,
  icon: <AttachMoneyIcon color='secondary' />,
  isMinMax: true,
  min: {
    inputLabel: MaFilterLabels.acquisitionCostMin,
    key: MaFilterKeys.acquisitionCostMin,
    default: 0,
  },
  max: {
    inputLabel: MaFilterLabels.acquisitionCostMax,
    key: MaFilterKeys.acquisitionCostMax,
    default: 0,
  },
  setDefault: {
    label: MaFilterLabels.excludeOutliers,
  },
};

export const PRICE_PER_BARREL_MIN_MAX = {
  label: MaFilterLabels.price,
  icon: <AttachMoneyIcon color='secondary' />,
  isMinMax: true,
  min: {
    inputLabel: MaFilterLabels.pricePerBarrelMin,
    key: MaFilterKeys.pricePerBarrelMin,
    default: 0,
  },
  max: {
    inputLabel: MaFilterLabels.pricePerBarrelMax,
    key: MaFilterKeys.pricePerBarrelMax,
    default: 0,
  },
  setDefault: {
    label: MaFilterLabels.excludeOutliers,
  },
};
